import { Component, OnDestroy, OnInit } from '@angular/core';
import { AlertController, ToastController } from '@ionic/angular';
import { Subscription } from 'rxjs';
import { GepjarmuService } from '../gepjarmu.service';
import { SzerkZar, SzerkZarService } from '../../components/szerk-zar/szerk-zar.service';

/*  GÉPJÁRMŰ – Járművek: a járműtörzs (a Kulcs-Autó törzsének és a Bér cégautó-törzsének utódja).
 *  A szerkesztő fülei: Alapadatok · Útnyilvántartás (hengerűrtartalom, norma, klíma, kezdő km, alap sofőr,
 *  telephely) · Cégautóadó adatai (kW, osztály, tulajdonviszony, használat – az adót a Cégautóadó képernyő
 *  számolja) · Óraállások · Kapcsolat (tárgyi eszköz). Szerkesztési zár: gepjarmu_jarmu.
 *  A rendszám nem írható át, ha már van rá út (a kiszolgáló dönt: backend/gepjarmu.js). */
type Ful = 'alap' | 'ut' | 'ado' | 'ora' | 'kapcs';

@Component({
  selector: 'app-gj-jarmuvek',
  templateUrl: './jarmuvek.component.html',
  styleUrls: ['../../naplo/naplo-shared.scss', '../gepjarmu-shared.scss'],
})
export class GjJarmuvekComponent implements OnInit, OnDestroy {
  jarmuvek: any[] = [];
  soforok: any[] = [];
  cimek: any[] = [];
  eszkozok: any[] = [];
  sz: any = null;               // szótárak (GET /szotarak)
  kereso = '';
  inaktiv = false;
  csoportSzuro = '';
  betolt = false;

  szerk: any = null;
  ful: Ful = 'alap';
  readonly fulek: { k: Ful; c: string }[] = [
    { k: 'alap', c: 'Alapadatok' }, { k: 'ut', c: 'Útnyilvántartás' }, { k: 'ado', c: 'Cégautóadó adatai' },
    { k: 'ora', c: 'Óraállások' }, { k: 'kapcs', c: 'Kapcsolat' },
  ];
  rendeleti: any = null;        // a rendelet szerinti alapnorma a beírt hengerűrtartalomhoz
  oraallasok: any[] = [];
  ujOra: any = null;
  zar: SzerkZar | null = null;
  ment = false;
  forgalmiFut = false;          // forgalmi-fotó OCR-feldolgozása folyamatban
  private zarKor = 0;
  private normaIdozito: any = null;
  private sub?: Subscription;

  constructor(public gj: GepjarmuService, private toast: ToastController, private alert: AlertController, private szerkZar: SzerkZarService) {}
  get u() { return this.gj.aktiv.ugyfelId; }
  get ir() { return this.gj.ir; }
  get zarolt() { return !!this.zar && !this.zar.irhato; }

  async ngOnInit() {
    await this.gj.init();
    try { this.sz = await this.gj.get('/szotarak'); } catch { this.sz = null; }
    this.sub = this.gj.aktiv$.subscribe(() => this.frissit());
  }
  ngOnDestroy() { this.sub?.unsubscribe(); this.zarElenged(); clearTimeout(this.normaIdozito); }

  async frissit() {
    if (!this.u) { this.jarmuvek = []; return; }
    this.betolt = true;
    try {
      const [j, s, c] = await Promise.all([this.gj.get(`/jarmuvek/${this.u}`), this.gj.get(`/soforok/${this.u}`), this.gj.get(`/cimek/${this.u}`)]);
      this.jarmuvek = j; this.soforok = s; this.cimek = c;
    } catch (e: any) { this.hiba(e?.error?.error || 'Nem sikerült betölteni a járműveket.'); }
    finally { this.betolt = false; }
  }
  get csoportok(): string[] { return [...new Set(this.jarmuvek.map((j) => j.csoport).filter(Boolean))].sort(); }
  get lathato(): any[] {
    const k = this.kereso.trim().toLowerCase().replace(/[\s-]/g, '');
    return this.jarmuvek.filter((j) => (this.inaktiv || j.aktiv) && (!this.csoportSzuro || j.csoport === this.csoportSzuro)
      && (!k || [j.rendszam, j.gyartmany, j.tipus, j.alap_sofor_nev].some((x) => String(x || '').toLowerCase().replace(/[\s-]/g, '').includes(k))));
  }
  get aktivSoforok() { return this.soforok.filter((s) => s.aktiv || s.id === this.szerk?.alap_sofor_id); }
  get telephelyek() { return this.cimek.filter((c) => (c.aktiv && c.fajta === 'telephely') || c.id === this.szerk?.telephely_cim_id); }
  get egyebCimek() { return this.cimek.filter((c) => c.aktiv && c.fajta !== 'telephely' && c.id !== this.szerk?.telephely_cim_id); }

  /* ── megjelenítő segédek ── */
  hajtasNev(v: string) { return this.sz?.hajtas?.[v] || v; }
  tvNev(v: string) { return this.sz?.tulajdonviszony?.[v]?.nev || v; }
  get tvLeiras(): string { return this.sz?.tulajdonviszony?.[this.szerk?.tulajdonviszony]?.leiras || ''; }
  normaSzoveg(n: any): string {
    if (!n || n.norma == null) return '—';
    return `${String(n.norma).replace('.', ',')} l/100 km`;
  }
  normaForras(f: string): string {
    return ({ kezi: 'kézi', rendelet: 'rendelet', elektromos: 'elektromos (3 l)', hianyzik: 'nincs cm³' } as any)[f] || f;
  }
  km(v: any) { return v == null ? '' : Number(v).toLocaleString('hu-HU'); }
  get szgk() { return this.szerk?.kategoria === 'szemely'; }

  /* ── szerkesztő ── */
  uj() {
    this.zarElenged();
    this.ful = 'alap';
    this.oraallasok = [];
    this.szerk = {
      id: null, rendszam: '', gyartmany: '', tipus: '', kategoria: 'szemely', hajtas: 'benzin', henger_cm3: null, teljesitmeny_kw: null,
      korny_osztaly: 15, korny_alkod: null, gyartasi_ev: null, tulajdonviszony: 'sajat', adoalany: true, tulajdoni_hanyad: 100, nem_nyilvantartott: false,
      nyilvantartasba_vetel: null, hasznalat_kezdete: null, hasznalat_vege: null, mentesseg: null, gepjarmuado_eves: 0, gepjarmuado_hatarido: true,
      norma_l100: null, klima: false, kezdo_km: null, alap_sofor_id: null, telephely_cim_id: this.telephelyek[0]?.id || null, csoport: this.csoportSzuro || '',
      eszkoz_id: null, aktiv: true, megjegyzes: '',
    };
    this.normaFrissit();
    this.eszkozBetolt();
  }
  async nyit(j: any) {
    this.ful = 'alap';
    this.szerk = this.szerkAdat(j);
    this.normaFrissit();
    this.eszkozBetolt();
    this.oraBetolt();
    this.zarFoglal(String(j.id));
  }
  private szerkAdat(j: any) {
    const b = (v: any) => !!Number(v);
    return { ...j, adoalany: b(j.adoalany), nem_nyilvantartott: b(j.nem_nyilvantartott), gepjarmuado_hatarido: b(j.gepjarmuado_hatarido),
      klima: b(j.klima), aktiv: b(j.aktiv), csoport: j.csoport || '', megjegyzes: j.megjegyzes || '' };
  }
  /*  SZERKESZTÉSI ZÁR (components/szerk-zar): a megnyitott járművet más közben nem írhatja felül. */
  private async zarFoglal(id: string) {
    this.zarElenged();
    if (!this.ir) return;
    const kor = this.zarKor;
    const z = await this.szerkZar.foglal('gepjarmu', 'gepjarmu_jarmu', id, { ujratolt: () => this.ujratolt(id) });
    if (kor !== this.zarKor || String(this.szerk?.id) !== id) { z?.felenged(); return; }
    this.zar = z;
  }
  private zarElenged() { this.zarKor++; this.zar?.felenged(); this.zar = null; }
  bezar() { this.szerk = null; this.ujOra = null; this.zarElenged(); }
  private async ujratolt(id: string) {
    const j = await this.gj.get(`/jarmu-adat/${id}`);
    if (String(this.szerk?.id) === id) { this.szerk = this.szerkAdat(j); this.oraallasok = j.oraallasok || []; this.normaFrissit(); }
  }
  private async eszkozBetolt() {
    try { this.eszkozok = await this.gj.get(`/eszkozok/${this.u}`); } catch { this.eszkozok = []; }
  }
  get eszkozValaszthato() { return this.eszkozok.filter((e) => !e.jarmu_id || e.jarmu_id === this.szerk?.id); }
  private async oraBetolt() {
    if (!this.szerk?.id) return;
    try { this.oraallasok = await this.gj.get(`/oraallasok/${this.szerk.id}`); } catch { this.oraallasok = []; }
  }
  /*  A rendelet szerinti norma a kiszolgálótól (egy helyen számolva) – gépelés közben kis késleltetéssel. */
  normaFrissit() {
    clearTimeout(this.normaIdozito);
    const s = this.szerk;
    if (!s) return;
    this.normaIdozito = setTimeout(async () => {
      try { this.rendeleti = await this.gj.get(`/norma?hajtas=${encodeURIComponent(s.hajtas || '')}&henger_cm3=${Number(s.henger_cm3) || 0}`); }
      catch { this.rendeleti = null; }
    }, 250);
  }
  get ervenyesNorma(): any {
    const k = Number(String(this.szerk?.norma_l100 ?? '').replace(',', '.'));
    if (this.szerk?.norma_l100 !== null && this.szerk?.norma_l100 !== '' && k > 0) return { norma: k, forras: 'kezi' };
    return this.rendeleti;
  }
  tulajdonviszonyValt(v: string) {
    this.szerk.tulajdonviszony = v;
    const t = this.sz?.tulajdonviszony?.[v];
    if (t) this.szerk.adoalany = !!t.adoalany;
  }
  osztalyValt(v: any) {
    this.szerk.korny_osztaly = Number(v);
    if (this.szerk.korny_osztaly !== 5) this.szerk.korny_alkod = null;
  }
  alkodValt(v: string | null) {
    this.szerk.korny_alkod = v;
    if (v === 'E' && this.szerk.hajtas !== 'elektromos') { this.szerk.hajtas = 'elektromos'; this.normaFrissit(); }
    if ((v === 'P' || v === 'N') && !['hibrid', 'plugin'].includes(this.szerk.hajtas)) { this.szerk.hajtas = 'plugin'; this.normaFrissit(); }
  }
  get alkodok(): { k: string; v: string }[] { return Object.entries(this.sz?.korny_alkod || {}).map(([k, v]) => ({ k, v: String(v) })); }
  get mentessegek(): { k: string; v: string }[] { return Object.entries(this.sz?.mentesseg || {}).map(([k, v]) => ({ k, v: String(v) })); }
  lista(obj: any): { k: string; v: string }[] { return Object.entries(obj || {}).map(([k, v]) => ({ k, v: String((v as any)?.nev ?? v) })); }

  /* ── forgalmi befotózása → OCR → az űrlap előtöltése (csak új járműnél) ── */
  async forgalmiValaszt(ev: Event) {
    const input = ev.target as HTMLInputElement;
    const f = input.files && input.files[0];
    input.value = '';   // ugyanaz a fájl újra választható legyen
    if (!f || !this.szerk || !this.u) return;
    this.forgalmiFut = true;
    try {
      const fd = new FormData();
      fd.append('kep', f, f.name || 'forgalmi.jpg');
      const r: any = await this.gj.postForm(`/forgalmi-ocr/${this.u}`, fd);
      const n = this.forgalmiElotolt(r?.mezok || {});
      this.ok(n ? `Forgalmi beolvasva – ${n} mező előtöltve. Kérlek ellenőrizd!` : 'A forgalmiból nem sikerült mezőt kiolvasni – töltsd ki kézzel.');
    } catch (e: any) {
      this.hiba(e?.error?.error || 'Nem sikerült feldolgozni a forgalmit.');
    } finally { this.forgalmiFut = false; }
  }
  /** A kiolvasott mezőket előtölti az űrlapba (csak a kapott, nem üres értékeket). Visszaadja: hány mező lett. */
  private forgalmiElotolt(m: any): number {
    const s = this.szerk; if (!s) return 0;
    let n = 0;
    const tolt = (k: string, v: any) => { if (v !== undefined && v !== null && v !== '') { s[k] = v; n++; } };
    tolt('rendszam', m.rendszam);
    tolt('gyartmany', m.gyartmany);
    tolt('tipus', m.tipus);
    if (m.kategoria && this.sz?.kategoria?.[m.kategoria]) { s.kategoria = m.kategoria; n++; }
    if (m.hajtas && this.sz?.hajtas?.[m.hajtas]) { s.hajtas = m.hajtas; n++; }
    tolt('henger_cm3', m.henger_cm3);
    tolt('teljesitmeny_kw', m.teljesitmeny_kw);
    if (m.korny_osztaly != null) { this.osztalyValt(m.korny_osztaly); n++; }
    tolt('gyartasi_ev', m.gyartasi_ev);
    tolt('nyilvantartasba_vetel', m.nyilvantartasba_vetel);
    //  Az alvázszámnak nincs külön mezője – a megjegyzésbe tesszük.
    if (m.alvazszam && !String(s.megjegyzes || '').includes(m.alvazszam)) {
      s.megjegyzes = (s.megjegyzes ? s.megjegyzes + ' · ' : '') + 'Alvázszám: ' + m.alvazszam; n++;
    }
    this.normaFrissit();
    return n;
  }

  async mentes() {
    if (this.zarolt || this.ment) return;
    const s = this.szerk;
    const body = { ...s, norma_l100: s.norma_l100 === '' ? null : s.norma_l100 };
    this.ment = true;
    try {
      const r: any = s.id ? await this.gj.put(`/jarmu/${s.id}`, body) : await this.gj.post(`/jarmu/${this.u}`, body);
      this.ok(s.id ? 'Jármű mentve.' : 'Jármű felvéve.');
      await this.frissit();
      if (!s.id) {
        //  Új jármű: az ablak nyitva marad (óraállás, kapcsolat már rögzíthető), a zárral együtt.
        const j = this.jarmuvek.find((x) => x.id === r.id);
        if (j) { await this.nyit(j); this.ful = 'alap'; } else this.bezar();
      } else this.bezar();
    } catch (e: any) { this.hiba(e?.error?.error || 'Nem sikerült menteni.'); }
    finally { this.ment = false; }
  }
  async torol() {
    if (this.zarolt || !this.szerk?.id) return;
    const s = this.szerk;
    const a = await this.alert.create({
      header: 'Jármű törlése', message: `Biztosan törlöd: ${s.rendszam}? Az óraállásai és lejáratai is törlődnek.`,
      buttons: [{ text: 'Mégse', role: 'cancel' }, { text: 'Törlés', role: 'destructive', handler: () => { this.torolVeg(s.id); } }],
    });
    await a.present();
  }
  private async torolVeg(id: string) {
    try { await this.gj.del(`/jarmu/${id}`); this.bezar(); this.ok('Jármű törölve.'); await this.frissit(); }
    catch (e: any) { this.hiba(e?.error?.error || 'Nem sikerült törölni.'); }
  }

  /* ── óraállások ── */
  oraUj() { this.ujOra = { datum: new Date().toISOString().slice(0, 10), km: null, fajta: 'egyeb', megjegyzes: '' }; }
  async oraMent() {
    if (!this.szerk?.id || !this.ujOra) return;
    try {
      await this.gj.post(`/oraallas/${this.szerk.id}`, this.ujOra);
      this.ujOra = null; await this.oraBetolt(); this.ok('Óraállás rögzítve.'); this.listaCsendesFrissit();
    } catch (e: any) { this.hiba(e?.error?.error || 'Nem sikerült rögzíteni.'); }
  }
  async oraTorol(o: any) {
    try { await this.gj.del(`/oraallas/${o.id}`); await this.oraBetolt(); this.listaCsendesFrissit(); }
    catch (e: any) { this.hiba(e?.error?.error || 'Nem sikerült törölni.'); }
  }
  private async listaCsendesFrissit() { try { this.jarmuvek = await this.gj.get(`/jarmuvek/${this.u}`); } catch { /* */ } }
  oraFajtak() { return this.lista(this.sz?.oraallas_fajta); }

  private async ok(m: string) { (await this.toast.create({ message: m, duration: 2600, color: 'success' })).present(); }
  private async hiba(m: string) { (await this.toast.create({ message: m, duration: 5200, color: 'danger' })).present(); }
}
